import { useState, useEffect, useCallback, useMemo } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../../services/api'
import { format, parseISO, isToday, isTomorrow, isPast, addHours } from 'date-fns'
import DatePicker from 'react-datepicker'
import { FALLBACK_IMAGE, applyFallbackIfNeeded } from '../../utils/imageFallback'
import { buildBlobUrl } from '../../utils/blobUrl'
import { useAccount } from '../../hooks/useAccount'
import ScheduleCoverageIndicator from '../shared/ScheduleCoverageIndicator'
import VideoSchedulingModal from './VideoSchedulingModal'
import {
  getSuggestedTimeSlots,
  filterAvailableTime,
  validateWithCascadeOption,
  validateScheduleTime,
  isTimeInQuietHours
} from '../../utils/scheduleValidation'
import { getDateLocale } from '../../utils/dateLocale'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

const STORY_STATUS = {
  SCHEDULED: 'scheduled',
  POSTED: 'posted',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
}

// Quick Time Selection Constants
const QUICK_TIME_SLOTS = [
  { labelKey: 'rescheduleModal.quickTime.morning', hour: 9, icon: '🌅' },
  { labelKey: 'rescheduleModal.quickTime.noon', hour: 12, icon: '☀️' },
  { labelKey: 'rescheduleModal.quickTime.evening', hour: 18, icon: '🌆' },
  { labelKey: 'rescheduleModal.quickTime.night', hour: 21, icon: '🌙' }
]

const TIME_INTERVALS = {
  MINUTES: 15,
  DEFAULT_HOUR: 9,
  DEFAULT_POST_INTERVAL: 24
}

// Sortable Story Card Component for Draft Mode
function SortableStoryCard({ story, hasDraftChange, displayDate, timeCategory, timeLabel, mediaUrl, isVideo, onStoryClick, t, i18n }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: story.story_id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-lg transition-all cursor-move relative group ${
        hasDraftChange
          ? 'border-orange-400 ring-2 ring-orange-200'
          : timeCategory === 'overdue'
          ? 'border-red-300 ring-2 ring-red-100'
          : timeCategory === 'today'
          ? 'border-blue-300 ring-2 ring-blue-100'
          : 'border-gray-200 hover:border-gray-300'
      } ${isDragging ? 'z-50' : ''}`}
      {...attributes}
      {...listeners}
    >
      {/* Drag Handle Icon - Smaller, only on hover */}
      <div className="absolute top-1 left-1 z-20 bg-white bg-opacity-90 rounded p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <svg className="h-3 w-3 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
        </svg>
      </div>

      {/* Media Preview */}
      <div className="aspect-story bg-gray-100 relative">
        {isVideo ? (
          <video
            src={mediaUrl}
            className="w-full h-full object-cover"
            muted
            playsInline
            preload="metadata"
          />
        ) : (
          <img
            src={mediaUrl}
            alt={story.caption_text || 'Story preview'}
            className="w-full h-full object-cover"
            onError={applyFallbackIfNeeded}
          />
        )}

        {/* Video Play Button Overlay */}
        {isVideo && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="bg-black bg-opacity-50 rounded-full p-3">
              <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        )}

        {/* Time Badge - Compact */}
        <div className="absolute bottom-1 left-1 right-1">
          <span
            className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold shadow-sm truncate w-full justify-center ${
              hasDraftChange
                ? 'bg-orange-600 text-white'
                : timeCategory === 'overdue'
                ? 'bg-red-600 text-white'
                : timeCategory === 'today'
                ? 'bg-blue-600 text-white'
                : timeCategory === 'tomorrow'
                ? 'bg-green-600 text-white'
                : 'bg-gray-800 text-white'
            }`}
          >
            {hasDraftChange && (
              <svg className="h-2.5 w-2.5 mr-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            )}
            {timeLabel}
          </span>
        </div>

        {/* Media Type Icon - Top Right Corner */}
        <div className="absolute top-1 right-1">
          {isVideo ? (
            <svg className="h-4 w-4 text-white drop-shadow-lg" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
          ) : (
            <svg className="h-4 w-4 text-white drop-shadow-lg opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          )}
        </div>
      </div>

      {/* Click overlay for editing (only when not dragging) */}
      <div
        className="absolute inset-0 cursor-pointer"
        onClick={(e) => {
          if (!isDragging) {
            onStoryClick(story, displayDate)
          }
        }}
        style={{ pointerEvents: isDragging ? 'none' : 'auto' }}
      />
    </div>
  )
}

function SchedulePage() {
  const { t, i18n } = useTranslation('schedule')
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { selectedAccountId, isLoadingAccounts } = useAccount()
  const [stories, setStories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedStory, setSelectedStory] = useState(null)
  const [rescheduleDate, setRescheduleDate] = useState(null)
  const [isRescheduling, setIsRescheduling] = useState(false)
  const [successMessage, setSuccessMessage] = useState(null)
  const [returningStoryId, setReturningStoryId] = useState(null)
  const [brandSettings, setBrandSettings] = useState(null)
  const [settingsLoading, setSettingsLoading] = useState(true)

  // Cascading reschedule state
  const [showCascadeConfirm, setShowCascadeConfirm] = useState(false)
  const [cascadeInfo, setCascadeInfo] = useState(null)

  // Draft mode state
  const [draftMode, setDraftMode] = useState(false)
  const [draftChanges, setDraftChanges] = useState([])
  const [showDraftPanel, setShowDraftPanel] = useState(false)
  const [isCommittingDrafts, setIsCommittingDrafts] = useState(false)
  const [showEditModeHelp, setShowEditModeHelp] = useState(false)

  // Video scheduling from Upload page
  const [showVideoScheduling, setShowVideoScheduling] = useState(false)
  const [videoSchedulingData, setVideoSchedulingData] = useState(null)

  // Drag and Drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  // Memoize quick time slot availability calculations (moved from inline to fix React hooks rules)
  const quickTimeSlotsWithAvailability = useMemo(() => {
    const now = new Date()
    const postIntervalHours = brandSettings?.post_interval_hours || TIME_INTERVALS.DEFAULT_POST_INTERVAL

    return QUICK_TIME_SLOTS.map(slot => {
      const proposedDate = new Date()
      proposedDate.setHours(slot.hour, 0, 0, 0)

      // If time is in the past, move to tomorrow
      if (proposedDate <= now) {
        proposedDate.setDate(proposedDate.getDate() + 1)
      }

      const isAvailable = filterAvailableTime(
        proposedDate,
        stories,
        postIntervalHours,
        selectedStory?.story_id
      )

      return {
        ...slot,
        proposedDate,
        isAvailable
      }
    })
  }, [stories, brandSettings?.post_interval_hours, selectedStory?.story_id])

  const loadScheduledStories = useCallback(async (abortedRef) => {
    try {
      setLoading(true)
      setError(null)

      const params = { status: STORY_STATUS.SCHEDULED }
      if (selectedAccountId) {
        params.instagram_account_id = selectedAccountId
      }

      const response = await api.getStories(params)

      // Check if request was aborted before updating state
      if (abortedRef?.current) return

      const scheduledStories = response.stories || []

      // Sort by scheduled_for (earliest first)
      scheduledStories.sort((a, b) => {
        const dateA = new Date(a.scheduled_for)
        const dateB = new Date(b.scheduled_for)
        return dateA - dateB
      })

      setStories(scheduledStories)
    } catch (err) {
      if (abortedRef?.current) return
      console.error('Failed to load scheduled stories:', err)
      setError(err.message || t('errors:schedule.loadFailed'))
    } finally {
      if (!abortedRef?.current) {
        setLoading(false)
      }
    }
  }, [selectedAccountId, t])

  const loadBrandSettings = useCallback(async () => {
    if (!selectedAccountId) {
      setBrandSettings({ post_interval_hours: 24 })
      setSettingsLoading(false)
      return
    }

    try {
      setSettingsLoading(true)
      const settings = await api.getBrandSettings(selectedAccountId)
      setBrandSettings(settings)
    } catch (err) {
      console.error('Failed to load brand settings:', err)
      // Set default if not found
      setBrandSettings({ post_interval_hours: 24 })
    } finally {
      setSettingsLoading(false)
    }
  }, [selectedAccountId])

  useEffect(() => {
    const abortedRef = { current: false };

    const loadData = async () => {
      if (!isLoadingAccounts) {
        await Promise.all([
          loadScheduledStories(abortedRef),
          loadBrandSettings()
        ]);
      }
    };

    loadData();

    return () => {
      abortedRef.current = true;
    };
  }, [selectedAccountId, isLoadingAccounts, loadScheduledStories, loadBrandSettings])

  // Check for video scheduling data from Upload page OR from Approval page query params
  useEffect(() => {
    const loadVideoFromApprovalQueue = async () => {
      const videoId = searchParams.get('videoId')
      const accountId = searchParams.get('instagram_account_id') || selectedAccountId

      if (!videoId) return

      try {
        // Fetch video metadata from backend
        const params = {}
        if (accountId) {
          params.instagram_account_id = accountId
        }

        // Get the unified approval queue to find the video
        const result = await api.getApprovals(params)
        const videos = result.items?.filter(item => item.type === 'video') || []
        const video = videos.find(v => v.video_id === videoId)

        if (video) {
          // Pre-populate video scheduling modal
          setVideoSchedulingData({
            videoData: video,
            file: null, // No file object when coming from approval queue
          })
          setShowVideoScheduling(true)

          // Clear query params to prevent modal from reopening on refresh
          window.history.replaceState({}, document.title)
        } else {
          console.warn(`Video ${videoId} not found in approval queue`)
        }
      } catch (err) {
        console.error('Failed to load video from approval queue:', err)
        // Non-blocking error - don't prevent page load
      }
    }

    if (location.state?.videoData) {
      // Original flow: from Upload page via location.state
      setVideoSchedulingData({
        videoData: location.state.videoData,
        file: location.state.file,
      })
      setShowVideoScheduling(true)

      // Clear location state to prevent modal from reopening on refresh
      window.history.replaceState({}, document.title)
    } else {
      // New flow: from Approval page via query params
      loadVideoFromApprovalQueue()
    }
  }, [searchParams, selectedAccountId])

  const handleReschedule = async (forceCascade = false) => {
    if (!selectedStory || !rescheduleDate || !selectedAccountId) {
      return
    }

    // In draft mode: add to draft changes instead of saving immediately
    if (draftMode) {
      const existingIndex = draftChanges.findIndex(
        change => change.story_id === selectedStory.story_id
      )

      const newChange = {
        story_id: selectedStory.story_id,
        old_time: parseISO(selectedStory.scheduled_for),
        new_time: rescheduleDate,
        instagram_account_id: selectedAccountId,
        caption_text: selectedStory.caption_text || t('storyCard.noCaption'),
        media_type: selectedStory.media_type || 'image'
      }

      if (existingIndex >= 0) {
        // Update existing draft change
        const updatedChanges = [...draftChanges]
        updatedChanges[existingIndex] = newChange
        setDraftChanges(updatedChanges)
      } else {
        // Add new draft change (max 350)
        if (draftChanges.length >= 350) {
          setError(t('errors.maxDraftChanges'))
          return
        }
        setDraftChanges([...draftChanges, newChange])
      }

      // Update story in local list to show draft state
      const updatedStories = stories.map(story =>
        story.story_id === selectedStory.story_id
          ? { ...story, draft_scheduled_for: rescheduleDate.toISOString() }
          : story
      )
      setStories(updatedStories)

      setSuccessMessage(t('editMode.addedToDraft', { date: format(rescheduleDate, 'PPp', { locale: getDateLocale(i18n.language) }), count: draftChanges.length + 1 }))
      setSelectedStory(null)
      setRescheduleDate(null)
      return
    }

    const postIntervalHours = brandSettings?.post_interval_hours || 24

    // ✅ Check if we need cascading
    const validation = validateWithCascadeOption(
      rescheduleDate,
      selectedStory.story_id,
      stories,
      postIntervalHours
    )

    // If valid, proceed normally
    if (validation.isValid) {
      try {
        setIsRescheduling(true)
        setError(null)

        const payload = {
          scheduled_for: rescheduleDate.toISOString(),
          instagram_account_id: selectedAccountId,  // ⭐ FIX: Always include instagram_account_id (backend requires it)
        }

        await api.rescheduleStory(selectedStory.story_id, payload)

        setSuccessMessage(t('messages.rescheduled', { date: format(rescheduleDate, 'PPp', { locale: getDateLocale(i18n.language) }) }))
        setSelectedStory(null)
        setRescheduleDate(null)

        await loadScheduledStories()
      } catch (err) {
        console.error('Failed to reschedule story:', err)
        setError(err.message || t('errors:schedule.rescheduleFailed'))
      } finally {
        setIsRescheduling(false)
      }
      return
    }

    // If can cascade and user hasn't confirmed yet, show confirmation modal
    if (validation.canCascade && !forceCascade) {
      setCascadeInfo(validation.cascadeInfo)
      setShowCascadeConfirm(true)
      return
    }

    // If forceCascade=true, perform bulk reschedule
    if (validation.canCascade && forceCascade) {
      try {
        setIsRescheduling(true)
        setError(null)

        // Build reschedules array: [current story + all affected stories]
        const reschedules = [
          {
            story_id: selectedStory.story_id,
            scheduled_for: rescheduleDate.toISOString()
          },
          ...validation.cascadeInfo.newSchedules.map(schedule => ({
            story_id: schedule.storyId,
            scheduled_for: schedule.newDate.toISOString()
          }))
        ]

        console.log('[SchedulePage] bulkRescheduleStories - selectedAccountId:', selectedAccountId)
        await api.bulkRescheduleStories(selectedAccountId, reschedules)

        const movedCount = validation.cascadeInfo.newSchedules.length
        setSuccessMessage(t('messages.rescheduledWithCascade', { count: movedCount }))
        setSelectedStory(null)
        setRescheduleDate(null)
        setShowCascadeConfirm(false)
        setCascadeInfo(null)

        await loadScheduledStories()
      } catch (err) {
        console.error('Failed to bulk reschedule stories:', err)
        setError(err.message || t('errors:schedule.rescheduleFailed'))
      } finally {
        setIsRescheduling(false)
      }
      return
    }

    // Can't cascade (conflict with story before)
    setError(t(validation.reasonKey, validation.reasonParams))
  }

  const commitDraftChanges = async () => {
    if (draftChanges.length === 0 || !selectedAccountId) return

    try {
      setIsCommittingDrafts(true)
      setError(null)

      // Prepare reschedules array for bulk API call
      const reschedules = draftChanges.map(change => ({
        story_id: change.story_id,
        scheduled_for: change.new_time.toISOString()
      }))

      const response = await api.bulkRescheduleStories(selectedAccountId, reschedules)

      // Handle multi-status response
      if (response.status === 207 && response.results) {
        const successCount = response.results.success?.length || 0
        const failedCount = response.results.failed?.length || 0

        if (failedCount > 0) {
          setError(t('messages.partialSuccess', { successCount, failedCount }))
        } else {
          setSuccessMessage(t('messages.allSaved', { count: successCount }))
        }
      } else {
        setSuccessMessage(t('messages.allSaved', { count: draftChanges.length }))
      }

      // Clear draft state and exit draft mode
      setDraftChanges([])
      setDraftMode(false)
      setShowDraftPanel(false)

      // Reload stories to show updated schedule
      await loadScheduledStories()
    } catch (err) {
      console.error('Failed to commit draft changes:', err)
      setError(err.message || t('errors.saveDraftChanges'))
    } finally {
      setIsCommittingDrafts(false)
    }
  }

  const removeDraftChange = (storyId) => {
    setDraftChanges(prev => prev.filter(change => change.story_id !== storyId))

    // Also remove draft_scheduled_for from stories
    const updatedStories = stories.map(story =>
      story.story_id === storyId
        ? { ...story, draft_scheduled_for: undefined }
        : story
    )
    setStories(updatedStories)
  }

  // Handle drag and drop reordering in draft mode
  const handleDragEnd = (event) => {
    const { active, over } = event

    if (!over || active.id === over.id) {
      return
    }

    const oldIndex = stories.findIndex(story => story.story_id === active.id)
    const newIndex = stories.findIndex(story => story.story_id === over.id)

    if (oldIndex === -1 || newIndex === -1) {
      return
    }

    // Reorder stories array
    const reorderedStories = arrayMove(stories, oldIndex, newIndex)
    setStories(reorderedStories)

    // Calculate new time slots based on position
    const postIntervalHours = brandSettings?.post_interval_hours || 24
    const quietHours = brandSettings?.posting_schedule?.quiet_hours || []
    const accountTimezone = brandSettings?.timezone || 'UTC'
    const now = new Date()

    // Find the earliest scheduled time or use now as base
    let baseTime = reorderedStories[0]?.scheduled_for
      ? parseISO(reorderedStories[0].scheduled_for)
      : now

    // If base time is in the past, use now
    if (baseTime < now) {
      baseTime = now
    }

    // Helper to find next valid slot (outside quiet hours)
    const findNextValidSlot = (proposedTime) => {
      let candidateTime = proposedTime
      let maxAttempts = 48 // Max 48 hours of searching
      let attempts = 0

      while (attempts < maxAttempts) {
        const quietCheck = isTimeInQuietHours(candidateTime, quietHours, accountTimezone)
        if (!quietCheck.isInQuietHours) {
          return candidateTime
        }
        // Move forward by 1 hour
        candidateTime = addHours(candidateTime, 1)
        attempts++
      }
      return candidateTime // Fallback
    }

    // Generate new draft changes for all stories based on their new position
    const newDraftChanges = []
    let lastScheduledTime = null // Track last scheduled time to avoid collisions

    reorderedStories.forEach((story, index) => {
      // Calculate new time slot: baseTime + (index * interval)
      let newTime = addHours(baseTime, index * postIntervalHours)

      // Apply quiet hours filtering
      newTime = findNextValidSlot(newTime)

      // Ensure no collision with previous story (must be at least postIntervalHours apart)
      if (lastScheduledTime) {
        const minNextTime = addHours(lastScheduledTime, postIntervalHours)
        if (newTime < minNextTime) {
          newTime = findNextValidSlot(minNextTime)
        }
      }

      lastScheduledTime = newTime
      const oldTime = story.draft_scheduled_for
        ? parseISO(story.draft_scheduled_for)
        : parseISO(story.scheduled_for)

      // Only add to draft if time changed
      if (newTime.getTime() !== oldTime.getTime()) {
        // Check if this story already has a draft change
        const existingDraftIndex = draftChanges.findIndex(
          change => change.story_id === story.story_id
        )

        const draftChange = {
          story_id: story.story_id,
          old_time: parseISO(story.scheduled_for), // Always use original scheduled_for
          new_time: newTime,
          instagram_account_id: selectedAccountId,
          caption_text: story.caption_text || t('storyCard.noCaption'),
          media_type: story.media_type || 'image'
        }

        newDraftChanges.push(draftChange)

        // Update story with draft time
        story.draft_scheduled_for = newTime.toISOString()
      }
    })

    // Merge with existing draft changes (replacing duplicates)
    const mergedDraftChanges = [...draftChanges]
    newDraftChanges.forEach(newChange => {
      const existingIndex = mergedDraftChanges.findIndex(
        existing => existing.story_id === newChange.story_id
      )
      if (existingIndex >= 0) {
        mergedDraftChanges[existingIndex] = newChange
      } else {
        if (mergedDraftChanges.length < 350) {
          mergedDraftChanges.push(newChange)
        }
      }
    })

    setDraftChanges(mergedDraftChanges)
    setSuccessMessage(
      t('editMode.reordered', {
        count: mergedDraftChanges.length,
        defaultValue: `${mergedDraftChanges.length} stories reordered`
      })
    )
  }

  const handleReturnToApproval = async (story) => {
    if (!story || !story.story_id) {
      setError(t('messages.noStory'))
      return
    }

    if (!selectedAccountId) {
      setError(t('messages.noAccount'))
      return
    }

    try {
      setReturningStoryId(story.story_id)
      setError(null)

      await api.returnStoryToApproval(story.story_id, {
        instagram_account_id: selectedAccountId,
      })

      setSuccessMessage(t('messages.movedToApproval'))
      setSelectedStory(null)
      setRescheduleDate(null)
      await loadScheduledStories()
    } catch (err) {
      console.error('Failed to return story to approval:', err)
      setError(err.message || t('errors.moveToApprovalFailed'))
    } finally {
      setReturningStoryId(null)
    }
  }

  const getTimeLabel = (scheduledFor) => {
    const date = parseISO(scheduledFor)

    if (isPast(date)) {
      return t('timeLabels.overdue')
    }
    if (isToday(date)) {
      return t('timeLabels.today', { time: format(date, 'HH:mm', { locale: getDateLocale(i18n.language) }) })
    }
    if (isTomorrow(date)) {
      return t('timeLabels.tomorrow', { time: format(date, 'HH:mm', { locale: getDateLocale(i18n.language) }) })
    }
    return t('timeLabels.upcoming', { date: format(date, 'MMM d, HH:mm', { locale: getDateLocale(i18n.language) }) })
  }

  const getTimeCategory = (scheduledFor) => {
    const date = parseISO(scheduledFor)

    if (isPast(date)) return 'overdue'
    if (isToday(date)) return 'today'
    if (isTomorrow(date)) return 'tomorrow'
    return 'upcoming'
  }

  const getMediaUrl = (story) => {
    // Priority order (same as ApprovalPage):
    // 1. SAS URLs (pre-signed, secure, direct access)
    // 2. Preview URLs / Blob URLs
    // 3. buildBlobUrl() helper (generates SAS URLs)
    // 4. Fallback image
    return story.rendered_sas_url ||
           story.sas_url ||
           story.preview_url ||
           story.blob_url ||
           buildBlobUrl(story.rendered_blob_path) ||
           buildBlobUrl(story.blob_path) ||
           FALLBACK_IMAGE
  }

  const isVideoStory = (story) => {
    return story.media_type === 'video'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('page.loading')}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Schedule Coverage Indicator */}
      <ScheduleCoverageIndicator
        scheduledCount={stories.length}
        postIntervalHours={brandSettings?.post_interval_hours || 24}
        loading={settingsLoading || loading}
        variant="compact"
        showCTA={true}
      />

      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('page.title')}</h1>
            <p className="mt-1 text-sm text-gray-600">
              {t('page.subtitle')}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            {/* Edit Schedule Mode Toggle */}
            <button
              onClick={() => {
                if (draftMode && draftChanges.length > 0) {
                  // If exiting edit mode with unsaved changes, show warning
                  if (window.confirm(t('editMode.confirmDiscard', { count: draftChanges.length }))) {
                    setDraftMode(false)
                    setDraftChanges([])
                    setShowDraftPanel(false)
                    setShowEditModeHelp(false)
                  }
                } else {
                  const newMode = !draftMode
                  setDraftMode(newMode)
                  setShowDraftPanel(false)
                  // Show help banner when activating edit mode
                  if (newMode) {
                    setShowEditModeHelp(true)
                  }
                }
              }}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                draftMode
                  ? 'bg-orange-500 text-white hover:bg-orange-600 ring-2 ring-orange-300'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <div className="flex items-center space-x-2">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <span>{t('editMode.inactive')}</span>
                {draftMode && draftChanges.length > 0 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-white text-orange-600">
                    {draftChanges.length}
                  </span>
                )}
              </div>
            </button>

            {/* Show Draft Panel Button (only when draft mode is active and has changes) */}
            {draftMode && draftChanges.length > 0 && (
              <button
                onClick={() => setShowDraftPanel(!showDraftPanel)}
                className="px-3 py-2 text-sm font-medium bg-orange-100 text-orange-700 hover:bg-orange-200 rounded-lg transition-colors"
              >
                {showDraftPanel ? t('editMode.panelClose') : t('editMode.showChanges', { count: draftChanges.length })}
              </button>
            )}

            <button
              onClick={loadScheduledStories}
              className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
              {t('page.count', { count: stories.length })}
            </span>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start justify-between">
          <div>
            <p className="font-medium">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-sm font-medium text-red-600 hover:text-red-800"
          >
            {t('dismiss')}
          </button>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg flex items-start justify-between">
          <p className="text-sm font-medium">{successMessage}</p>
          <button
            onClick={() => setSuccessMessage(null)}
            className="text-xs font-semibold uppercase tracking-wide text-green-700 hover:text-green-900"
          >
            {t('dismiss')}
          </button>
        </div>
      )}

      {/* Edit Mode Help Banner */}
      {draftMode && showEditModeHelp && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-5 shadow-sm">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-3">
                <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-lg font-semibold text-gray-900">{t('editMode.helpTitle')}</h3>
              </div>
              <div className="space-y-2 ml-8">
                <div className="flex items-start space-x-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex-shrink-0 mt-0.5">1</span>
                  <p className="text-sm text-gray-700">{t('editMode.helpStep1')}</p>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex-shrink-0 mt-0.5">2</span>
                  <p className="text-sm text-gray-700">{t('editMode.helpStep2', { hours: brandSettings?.post_interval_hours || 24 })}</p>
                </div>
                <div className="flex items-start space-x-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex-shrink-0 mt-0.5">3</span>
                  <p className="text-sm text-gray-700">{t('editMode.helpStep3')}</p>
                </div>
              </div>
            </div>
            <button
              onClick={() => setShowEditModeHelp(false)}
              className="ml-4 px-4 py-2 text-sm font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors"
            >
              {t('editMode.helpDismiss')}
            </button>
          </div>
        </div>
      )}

      {/* Stories Grid */}
      {stories.length > 0 ? (
        draftMode ? (
          // Draft Mode: Sortable vertical list with drag & drop
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-lg p-6 border-2 border-dashed border-orange-300">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <svg className="h-5 w-5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                  </svg>
                  <h3 className="text-lg font-semibold text-orange-900">
                    {t('editMode.dragToReorder', { defaultValue: 'Drag to Reorder Timeline' })}
                  </h3>
                </div>
                <span className="text-sm text-orange-700">
                  {t('editMode.intervalInfo', {
                    hours: brandSettings?.post_interval_hours || 24,
                    defaultValue: `${brandSettings?.post_interval_hours || 24}h intervals`
                  })}
                </span>
              </div>

              <SortableContext
                items={stories.map(s => s.story_id)}
                strategy={rectSortingStrategy}
              >
                {/* Grid Layout: 2 columns mobile, 6 columns desktop */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                  {stories.map((story, index) => {
                    const hasDraftChange = draftChanges.some(change => change.story_id === story.story_id)
                    const displayDate = story.draft_scheduled_for || story.scheduled_for
                    const timeCategory = getTimeCategory(displayDate)
                    const timeLabel = getTimeLabel(displayDate)
                    const mediaUrl = getMediaUrl(story)
                    const isVideo = isVideoStory(story)

                    return (
                      <div key={story.story_id} className="relative">
                        {/* Position Indicator - Top Badge */}
                        <div className="absolute -top-2 -left-2 z-30 w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold text-sm shadow-lg border-2 border-white">
                          {index + 1}
                        </div>

                        {/* Sortable Story Card */}
                        <SortableStoryCard
                          story={story}
                          hasDraftChange={hasDraftChange}
                          displayDate={displayDate}
                          timeCategory={timeCategory}
                          timeLabel={timeLabel}
                          mediaUrl={mediaUrl}
                          isVideo={isVideo}
                          onStoryClick={(story, displayDate) => {
                            setSelectedStory(story)
                            setRescheduleDate(parseISO(displayDate))
                          }}
                          t={t}
                          i18n={i18n}
                        />
                      </div>
                    )
                  })}
                </div>
              </SortableContext>
            </div>
          </DndContext>
        ) : (
          // Normal Mode: Grid layout (same as edit mode for consistency)
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
            {stories.map((story) => {
              const hasDraftChange = draftChanges.some(change => change.story_id === story.story_id)
              const displayDate = story.draft_scheduled_for || story.scheduled_for
              const timeCategory = getTimeCategory(displayDate)
              const timeLabel = getTimeLabel(displayDate)
              const mediaUrl = getMediaUrl(story)
              const isVideo = isVideoStory(story)

              return (
                <div
                  key={story.story_id}
                  className={`bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-lg transition-all cursor-pointer relative ${
                    hasDraftChange
                      ? 'border-orange-400 ring-2 ring-orange-200'
                      : timeCategory === 'overdue'
                      ? 'border-red-300 ring-2 ring-red-100'
                      : timeCategory === 'today'
                      ? 'border-blue-300 ring-2 ring-blue-100'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => {
                    setSelectedStory(story)
                    setRescheduleDate(parseISO(displayDate))
                  }}
                >
                  {/* Media Preview */}
                  <div className="aspect-story bg-gray-100 relative">
                    {isVideo ? (
                      <video
                        src={mediaUrl}
                        className="w-full h-full object-cover"
                        muted
                        playsInline
                        preload="metadata"
                      />
                    ) : (
                      <img
                        src={mediaUrl}
                        alt={story.caption_text || 'Story preview'}
                        className="w-full h-full object-cover"
                        onError={applyFallbackIfNeeded}
                      />
                    )}

                    {/* Video Play Button Overlay */}
                    {isVideo && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="bg-black bg-opacity-50 rounded-full p-3">
                          <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    )}

                    {/* Time Badge - Compact, at bottom */}
                    <div className="absolute bottom-1 left-1 right-1">
                      <span
                        className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold shadow-sm truncate w-full justify-center ${
                          hasDraftChange
                            ? 'bg-orange-600 text-white'
                            : timeCategory === 'overdue'
                            ? 'bg-red-600 text-white'
                            : timeCategory === 'today'
                            ? 'bg-blue-600 text-white'
                            : timeCategory === 'tomorrow'
                            ? 'bg-green-600 text-white'
                            : 'bg-gray-800 text-white'
                        }`}
                      >
                        {hasDraftChange && (
                          <svg className="h-2.5 w-2.5 mr-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        )}
                        {timeLabel}
                      </span>
                    </div>

                    {/* Media Type Icon - Top Right Corner */}
                    <div className="absolute top-1 right-1">
                      {isVideo ? (
                        <svg className="h-4 w-4 text-white drop-shadow-lg" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      ) : (
                        <svg className="h-4 w-4 text-white drop-shadow-lg opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
          <div className="text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('empty.title')}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {t('empty.subtitle')}
            </p>
          </div>
        </div>
      )}

      {/* Draft Changes Panel */}
      {showDraftPanel && draftChanges.length > 0 && (
        <div className="fixed top-0 right-0 h-full w-full sm:w-96 bg-white shadow-2xl z-40 flex flex-col border-l border-gray-200">
          {/* Panel Header */}
          <div className="bg-orange-50 border-b border-orange-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{t('draftPanel.title')}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {t('draftPanel.subtitle', { count: draftChanges.length })}
                </p>
              </div>
              <button
                onClick={() => setShowDraftPanel(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Draft Changes List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {draftChanges.map((change, idx) => {
              const story = stories.find(s => s.story_id === change.story_id)
              if (!story) return null

              return (
                <div key={change.story_id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-xs font-semibold text-gray-500">#{idx + 1}</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                          {change.media_type === 'video' ? t('mediaType.video') : t('mediaType.image')}
                        </span>
                      </div>

                      {/* Caption Preview */}
                      <p className="text-xs text-gray-600 line-clamp-2 mb-2">
                        {change.caption_text}
                      </p>

                      {/* Time Change */}
                      <div className="flex items-center space-x-2 text-xs">
                        <div className="flex items-center space-x-1">
                          <span className="text-gray-500">{t('draftPanel.from')}</span>
                          <span className="font-medium text-gray-700">
                            {format(change.old_time, 'dd.MM. HH:mm', { locale: getDateLocale(i18n.language) })}
                          </span>
                        </div>
                        <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                        <div className="flex items-center space-x-1">
                          <span className="text-gray-500">{t('draftPanel.to')}</span>
                          <span className="font-medium text-orange-600">
                            {format(change.new_time, 'dd.MM. HH:mm', { locale: getDateLocale(i18n.language) })}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Remove Button */}
                    <button
                      onClick={() => removeDraftChange(change.story_id)}
                      className="ml-2 p-2 -m-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                      title={t('draftPanel.discardChange')}
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Panel Footer with Actions */}
          <div className="border-t border-gray-200 bg-gray-50 p-4 space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">{t('draftPanel.total')}</span>
              <span className="font-semibold text-gray-900">
                {t('draftPanel.changesCount', { count: draftChanges.length })}
              </span>
            </div>

            <div className="flex space-x-3">
              <button
                onClick={() => {
                  if (window.confirm(t('draftPanel.confirmDiscardAll', { count: draftChanges.length }))) {
                    setDraftChanges([])
                    // Clear all draft_scheduled_for from stories
                    const updatedStories = stories.map(story => ({
                      ...story,
                      draft_scheduled_for: undefined
                    }))
                    setStories(updatedStories)
                    setShowDraftPanel(false)
                    setSuccessMessage(t('draftPanel.allDiscarded'))
                  }
                }}
                className="flex-1 px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
              >
                {t('draftPanel.discardAll')}
              </button>
              <button
                onClick={commitDraftChanges}
                disabled={isCommittingDrafts || draftChanges.length === 0}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCommittingDrafts ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('draftPanel.saving')}
                  </span>
                ) : (
                  t('draftPanel.saveAll', { count: draftChanges.length })
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Video Scheduling Modal */}
      {showVideoScheduling && videoSchedulingData && (
        <VideoSchedulingModal
          videoData={videoSchedulingData.videoData}
          file={videoSchedulingData.file}
          onClose={() => {
            setShowVideoScheduling(false)
            setVideoSchedulingData(null)
            // Reload stories to show the newly scheduled video
            loadScheduledStories()
          }}
        />
      )}

      {/* Reschedule Modal */}
      {selectedStory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center space-x-3">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('rescheduleModal.title')}
                </h3>
                {draftMode && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
                    <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    {t('editMode.active')}
                  </span>
                )}
              </div>
              <button
                onClick={() => {
                  setSelectedStory(null)
                  setRescheduleDate(null)
                }}
                className="text-gray-400 hover:text-gray-500"
                disabled={isRescheduling}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="overflow-y-auto flex-1 p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Media Preview */}
                <div>
                  <div className="aspect-story bg-gray-100 rounded-lg overflow-hidden">
                    {isVideoStory(selectedStory) ? (
                      <video
                        src={getMediaUrl(selectedStory)}
                        className="w-full h-full object-cover"
                        controls
                        playsInline
                        preload="metadata"
                      />
                    ) : (
                      <img
                        src={getMediaUrl(selectedStory)}
                        alt="Story preview"
                        className="w-full h-full object-cover"
                        onError={applyFallbackIfNeeded}
                      />
                    )}
                  </div>

                  {/* Story Info */}
                  <div className="mt-4 space-y-2">
                    {/* Media Type Badge */}
                    <div className="flex items-center space-x-2">
                      <span className="text-xs font-medium text-gray-500">{t('rescheduleModal.type.label')}</span>
                      {isVideoStory(selectedStory) ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gradient-to-r from-purple-100 to-pink-100 text-purple-800">
                          {t('rescheduleModal.type.video')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800">
                          {t('rescheduleModal.type.image')}
                        </span>
                      )}
                    </div>

                    {/* Video Metadata or Image Sentiment */}
                    {isVideoStory(selectedStory) ? (
                      <>
                        {selectedStory.video_duration && (
                          <div className="flex items-center space-x-2">
                            <span className="text-xs font-medium text-gray-500">{t('rescheduleModal.duration.label')}</span>
                            <span className="text-sm text-gray-700">{selectedStory.video_duration}s</span>
                          </div>
                        )}
                        {selectedStory.video_resolution && (
                          <div className="flex items-center space-x-2">
                            <span className="text-xs font-medium text-gray-500">{t('rescheduleModal.resolution.label')}</span>
                            <span className="text-sm text-gray-700">{selectedStory.video_resolution}</span>
                          </div>
                        )}
                        <div>
                          <p className="text-xs text-gray-500 italic">{t('rescheduleModal.captionEmbedded')}</p>
                        </div>
                      </>
                    ) : (
                      <>
                        {selectedStory.sentiment && (
                          <div className="flex items-center space-x-2">
                            <span className="text-xs font-medium text-gray-500">{t('rescheduleModal.sentiment.label')}</span>
                            <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-800">
                              {selectedStory.sentiment}
                            </span>
                          </div>
                        )}
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1">{t('rescheduleModal.caption.label')}</p>
                          <p className="text-sm text-gray-700 line-clamp-3">
                            {selectedStory.caption_text || t('storyCard.noCaption')}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Reschedule Form */}
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">
                      <span className="font-medium">{t('rescheduleModal.currentSchedule.label')}</span>
                    </p>
                    <p className="text-base font-semibold text-gray-900">
                      {format(parseISO(selectedStory.scheduled_for), 'EEEE, MMMM d, yyyy', { locale: getDateLocale(i18n.language) })}
                    </p>
                    <p className="text-lg font-bold text-primary-600">
                      {format(parseISO(selectedStory.scheduled_for), 'HH:mm', { locale: getDateLocale(i18n.language) })}
                    </p>
                  </div>

                  {/* Post Interval Info */}
                  {brandSettings?.post_interval_hours && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-xs font-medium text-blue-900">
                        {t('rescheduleModal.interval.info', { hours: brandSettings.post_interval_hours })}
                      </p>
                      <p className="text-xs text-blue-700 mt-1">
                        {t('rescheduleModal.interval.help')}
                      </p>
                    </div>
                  )}

                  {/* Suggested Time Slots */}
                  {(() => {
                    const postIntervalHours = brandSettings?.post_interval_hours || 24
                    const suggestions = getSuggestedTimeSlots(
                      stories.filter(s => s.story_id !== selectedStory.story_id),
                      postIntervalHours,
                      5
                    )

                    return suggestions.length > 0 && (
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-2">
                          {t('rescheduleModal.suggestedSlots.label')}
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {suggestions.map((slot, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => setRescheduleDate(slot.date)}
                              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                                rescheduleDate && rescheduleDate.getTime() === slot.date.getTime()
                                  ? 'bg-primary-600 text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              {slot.label}
                              <br />
                              <span className="text-[10px] opacity-75">
                                {format(slot.date, 'HH:mm', { locale: getDateLocale(i18n.language) })}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )
                  })()}

                  {/* Quick Time Selection */}
                  <div className="border-t border-gray-200 pt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Quick Time Selection
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                      {quickTimeSlotsWithAvailability.map((slot) => {
                        const isSelected = rescheduleDate &&
                          rescheduleDate.getHours() === slot.hour &&
                          rescheduleDate.getMinutes() === 0

                        return (
                          <button
                            key={slot.hour}
                            type="button"
                            disabled={!slot.isAvailable}
                            onClick={() => setRescheduleDate(new Date(slot.proposedDate))}
                            className={`
                              py-3 px-4 rounded-lg text-sm font-medium transition-all
                              ${isSelected
                                ? 'bg-purple-600 text-white ring-2 ring-purple-600 ring-offset-2'
                                : slot.isAvailable
                                ? 'bg-white text-gray-700 border-2 border-gray-300 hover:border-purple-400 hover:bg-purple-50'
                                : 'bg-gray-100 text-gray-400 border-2 border-gray-200 cursor-not-allowed opacity-60'
                              }
                            `}
                          >
                            <div className="flex flex-col items-center space-y-1">
                              <span className="text-lg">{slot.icon}</span>
                              <span>{t(slot.labelKey)}</span>
                              <span className="text-xs opacity-75">{slot.hour}:00</span>
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  {/* Custom Date & Time Selection */}
                  <div className="border-t border-gray-200 pt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      {t('rescheduleModal.customTime.label')}
                    </label>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      {/* Date Picker */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-2">
                          {t('rescheduleModal.datePicker.label')}
                        </label>
                        <DatePicker
                          selected={rescheduleDate}
                          onChange={(date) => {
                            if (rescheduleDate) {
                              // Preserve time when changing date
                              date.setHours(rescheduleDate.getHours())
                              date.setMinutes(rescheduleDate.getMinutes())
                            } else {
                              // Default to morning time if no time selected
                              date.setHours(TIME_INTERVALS.DEFAULT_HOUR, 0, 0, 0)
                            }
                            setRescheduleDate(date)
                          }}
                          minDate={new Date()}
                          dateFormat="EEEE, MMMM d, yyyy"
                          locale={getDateLocale(i18n.language)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-base"
                          placeholderText={t('rescheduleModal.datePicker.placeholder')}
                        />
                      </div>

                      {/* Time Picker */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-2">
                          {t('rescheduleModal.timePicker.label')}
                        </label>
                        <DatePicker
                          selected={rescheduleDate}
                          onChange={(date) => setRescheduleDate(date)}
                          showTimeSelect
                          showTimeSelectOnly
                          timeIntervals={TIME_INTERVALS.MINUTES}
                          timeCaption="Time"
                          dateFormat="HH:mm"
                          timeFormat="HH:mm"
                          filterTime={(time) => {
                            const postIntervalHours = brandSettings?.post_interval_hours || TIME_INTERVALS.DEFAULT_POST_INTERVAL
                            return filterAvailableTime(
                              time,
                              stories,
                              postIntervalHours,
                              selectedStory?.story_id
                            )
                          }}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-base"
                          placeholderText={t('rescheduleModal.timePicker.placeholder')}
                        />
                      </div>
                    </div>

                    {/* Validation Feedback */}
                    {rescheduleDate && (() => {
                      const postIntervalHours = brandSettings?.post_interval_hours || TIME_INTERVALS.DEFAULT_POST_INTERVAL
                      const validation = validateScheduleTime(
                        rescheduleDate,
                        stories,
                        postIntervalHours,
                        selectedStory?.story_id
                      )

                      if (!validation.isValid) {
                        return (
                          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm text-red-700">
                              {t('rescheduleModal.validation.conflict', { hours: postIntervalHours })}
                            </p>
                          </div>
                        )
                      }
                      return (
                        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                          <p className="text-sm text-green-700 font-medium">
                            {t('rescheduleModal.validation.success')}
                          </p>
                        </div>
                      )
                    })()}
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t border-gray-200 flex-shrink-0 z-10">
              <button
                onClick={() => handleReturnToApproval(selectedStory)}
                disabled={isRescheduling || returningStoryId === selectedStory.story_id}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-60"
              >
                {returningStoryId === selectedStory.story_id ? t('rescheduleModal.button.backToApprovalShort') : t('rescheduleModal.button.backToApproval')}
              </button>
              <button
                onClick={() => {
                  setSelectedStory(null)
                  setRescheduleDate(null)
                }}
                disabled={isRescheduling}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {t('rescheduleModal.button.cancel')}
              </button>
              <button
                onClick={handleReschedule}
                disabled={isRescheduling || !rescheduleDate}
                className={`px-6 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  draftMode
                    ? 'bg-orange-500 hover:bg-orange-600'
                    : 'bg-primary-600 hover:bg-primary-700'
                }`}
              >
                {isRescheduling
                  ? t('rescheduleModal.button.confirming')
                  : draftMode
                    ? t('editMode.addToDraft', { count: draftChanges.length })
                    : t('rescheduleModal.button.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Draft Commit Button (when panel is closed) */}
      {draftMode && draftChanges.length > 0 && !showDraftPanel && (
        <div className="fixed bottom-4 right-4 sm:bottom-8 sm:right-8 z-30">
          <div className="flex flex-col items-end space-y-3">
            <button
              onClick={commitDraftChanges}
              disabled={isCommittingDrafts}
              className="group relative px-6 py-3 text-sm font-medium text-white bg-green-600 rounded-full shadow-lg hover:bg-green-700 transition-all hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center space-x-2">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>
                  {isCommittingDrafts
                    ? t('draftPanel.saving')
                    : t('floatingActions.saveChanges', { count: draftChanges.length })}
                </span>
              </div>
              {/* Tooltip */}
              <div className="absolute bottom-full right-0 mb-2 px-3 py-2 text-xs text-white bg-gray-800 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">
                {t('floatingActions.tooltip')}
              </div>
            </button>
            <button
              onClick={() => setShowDraftPanel(true)}
              className="px-5 py-2.5 text-sm font-medium text-orange-700 bg-orange-100 rounded-full shadow hover:bg-orange-200 transition-colors"
            >
              {t('floatingActions.viewChanges')}
            </button>
          </div>
        </div>
      )}

      {/* Cascade Confirmation Modal */}
      {showCascadeConfirm && cascadeInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-yellow-50">
              <div className="flex items-center space-x-2">
                <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('cascadeModal.title')}
                </h3>
              </div>
              <button
                onClick={() => {
                  setShowCascadeConfirm(false)
                  setCascadeInfo(null)
                }}
                className="text-gray-400 hover:text-gray-500"
                disabled={isRescheduling}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <p className="text-sm text-gray-700 mb-4">
                {t('cascadeModal.message', { hours: brandSettings?.post_interval_hours })}
              </p>
              <p className="text-sm text-gray-700 mb-6">
                {t('cascadeModal.question', { count: cascadeInfo.newSchedules.length })}
              </p>

              {/* Affected Stories List */}
              <div className="bg-gray-50 rounded-lg p-4 max-h-64 overflow-y-auto space-y-3">
                {cascadeInfo.newSchedules.map((schedule, idx) => {
                  const story = stories.find(s => (s.story_id || s.id) === schedule.storyId)
                  return (
                    <div key={schedule.storyId} className="bg-white rounded-lg p-3 shadow-sm">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {t('cascadeModal.storyLabel', { number: idx + 1 })}
                          </p>
                          {story?.caption_text && (
                            <p className="text-xs text-gray-600 line-clamp-1 mt-1">
                              {story.caption_text}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="mt-2 flex items-center space-x-2 text-xs">
                        <div className="flex items-center space-x-1">
                          <span className="text-gray-500">{t('cascadeModal.from')}</span>
                          <span className="font-medium text-gray-700">
                            {format(schedule.oldDate, 'dd.MM. HH:mm', { locale: getDateLocale(i18n.language) })}
                          </span>
                        </div>
                        <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                        <div className="flex items-center space-x-1">
                          <span className="text-gray-500">{t('cascadeModal.to')}</span>
                          <span className="font-medium text-primary-600">
                            {format(schedule.newDate, 'dd.MM. HH:mm', { locale: getDateLocale(i18n.language) })}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500 ml-auto">
                          {t('cascadeModal.shift', { hours: schedule.hoursShifted.toFixed(1) })}
                        </span>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowCascadeConfirm(false)
                  setCascadeInfo(null)
                }}
                disabled={isRescheduling}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {t('cascadeModal.button.cancel')}
              </button>
              <button
                onClick={() => handleReschedule(true)}
                disabled={isRescheduling}
                className="px-6 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRescheduling ? t('rescheduleModal.button.confirming') : t('cascadeModal.button.confirm', { count: cascadeInfo.newSchedules.length })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SchedulePage
